// ---------------------------------------------------------------------------
// FEMA IPAWS — the TENANT and CREDENTIAL shapes (card FE-11, multi-COG program).
//
// Source: MULTICOG-CONTRACT §3 (expiry bands), §4 (credential state), §5 (`ZCredentialStage`,
// `ZCredentialInspection`, `ZCredentialFacts`, `ZFemaTenantSummary`, the refusal body). The
// Fusion edge mirror is `/fusion/v2/fema/tenants…`, scoped to the session workplace and gated on
// `ManageFemaCredentials`.
//
// The backend routes do not exist yet. Every shape below is a mirror of the contract, field for
// field. No shape carries a password, key bytes, PEM text or a secret value.
// ---------------------------------------------------------------------------

/**
 * Contract §0: the class tokens on the wire (the DB enum). Lower case. This is the ONE class list
 * of the front end. `types/fema.types.ts` and the Alert Creator config use aliases of this array.
 */
export const FEMA_ENVIRONMENT_CLASSES = ['tdl', 'demo', 'production'] as const;
export type FemaEnvironmentClass = (typeof FEMA_ENVIRONMENT_CLASSES)[number];

/**
 * Contract §4 `ZCredentialState`: ONE tenant-level state for every UI. The FE-11 card names map
 * as the contract says: `not_configured` → `none`, `uploaded` → `stored`. `validated` and
 * `retired` are new. This is the ONE list of states. `FemaMetaCredentialState` in
 * `types/fema.types.ts` is an alias of `FemaCredentialState`.
 */
export const FEMA_CREDENTIAL_STATES = [
  'none',
  'stored',
  'validated',
  'active',
  'expiring',
  'expired',
  'failed',
  'retired',
] as const;
export type FemaCredentialState = (typeof FEMA_CREDENTIAL_STATES)[number];

/** Contract §2/§3 `ZExpiryBand`. */
export const FEMA_EXPIRY_BANDS = ['ok', '90', '60', '30', '14', '7', 'expired'] as const;
export type FemaExpiryBand = (typeof FEMA_EXPIRY_BANDS)[number];

/** Contract §5 `ZCredentialStage`: the stage that a credential failure names. Never a value. */
export const FEMA_CREDENTIAL_STAGES = [
  'container-format',
  'container-password',
  'key-alias',
  'key-password',
  'certificate-parse',
  'key-certificate-mismatch',
  'cog-mismatch',
  'expired',
  'not-yet-valid',
  'store-write',
  'registry-write',
  'handshake',
] as const;
export type FemaCredentialStage = (typeof FEMA_CREDENTIAL_STAGES)[number];

/** Contract §5 `ZCredentialInspection`: the facts of an inspected (not stored) credential. */
export type FemaCredentialInspection = {
  subject: string;
  issuer: string;
  serialNumber: string;
  /** Lower-case hex, 64 characters. */
  sha256: string;
  notBefore: string;
  notAfter: string;
  daysToExpiry: number;
  keyAlgorithm: string;
  chainLength: number;
  /** `null` when the tenant has no COG to compare. */
  cogMatches: boolean | null;
};

/** Contract §5 `ZCredentialFacts`: the inspection facts a stored credential keeps. */
export type FemaCredentialFacts = Pick<
  FemaCredentialInspection,
  'subject' | 'issuer' | 'serialNumber' | 'sha256' | 'notBefore' | 'notAfter' | 'daysToExpiry'
> & {
  expiryBand: FemaExpiryBand | null;
};

/** Contract §5 `ZFemaTenantSummary`: the edge answer for ONE tenant of the session workplace. */
export type FemaTenantSummary = {
  id: number;
  environment: FemaEnvironmentClass;
  isProduction: boolean;
  cogId: string;
  cogName: string | null;
  enabled: boolean;
  credentialState: FemaCredentialState;
  daysToExpiry: number | null;
  expiryBand: FemaExpiryBand | null;
  lastValidatedAt: string | null;
  failedStage: FemaCredentialStage | null;
  active: FemaCredentialFacts | null;
  replacement: (FemaCredentialFacts & { validatedAt: string | null }) | null;
};

/** Contract §5: the credential slot a store call wrote and a validate call reads. */
export type FemaCredentialSlotName = 'active' | 'pending';

/** Contract §5 `POST …/credential/inspect` → 200. */
export type FemaCredentialInspectAnswer = {
  inspection: FemaCredentialInspection;
  stored: false;
};

/** Contract §5 `POST …/credential` → 201. The edge answers the tenant as `FemaTenantSummary`. */
export type FemaCredentialStoreAnswer = {
  credential: {
    slot: FemaCredentialSlotName;
    generation: number;
    status: 'stored';
    certificate: FemaCredentialFacts;
  };
  tenant: FemaTenantSummary;
};

/** Contract §5 `POST …/validate` → 200 `validation`. */
export type FemaTenantValidation = {
  state: 'passed' | 'failed';
  at: string | null;
  slot: FemaCredentialSlotName;
  stage: FemaCredentialStage | null;
  error: string | null;
};

/** Contract §5 `POST …/validate` → 200. */
export type FemaTenantValidateAnswer = {
  tenant: FemaTenantSummary;
  validation: FemaTenantValidation;
};

/** Contract §5: the refusal body on every tenant route. */
export type FemaTenantRefusal = {
  code: string;
  message: string;
  stage?: FemaCredentialStage;
  findings?: unknown[];
};

// ---------------------------------------------------------------------------
// Structural guards. The FE has no Zod: each guard checks every field of the contract shape.
// ---------------------------------------------------------------------------

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isString = (value: unknown): value is string => typeof value === 'string';
const isStringOrNull = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';
const isInteger = (value: unknown): value is number => Number.isInteger(value);
const isIntegerOrNull = (value: unknown): value is number | null =>
  value === null || Number.isInteger(value);
const isOneOf =
  <T extends string>(values: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === 'string' && (values as readonly string[]).includes(value);

export const isFemaEnvironmentClass = isOneOf(FEMA_ENVIRONMENT_CLASSES);
export const isFemaCredentialState = isOneOf(FEMA_CREDENTIAL_STATES);
export const isFemaExpiryBand = isOneOf(FEMA_EXPIRY_BANDS);
export const isFemaCredentialStage = isOneOf(FEMA_CREDENTIAL_STAGES);

const SHA256_PATTERN = /^[0-9a-f]{64}$/;

export const isFemaCredentialFacts = (value: unknown): value is FemaCredentialFacts =>
  isRecord(value) &&
  isString(value.subject) &&
  isString(value.issuer) &&
  isString(value.serialNumber) &&
  isString(value.sha256) &&
  SHA256_PATTERN.test(value.sha256) &&
  isString(value.notBefore) &&
  isString(value.notAfter) &&
  isInteger(value.daysToExpiry) &&
  (value.expiryBand === null || isFemaExpiryBand(value.expiryBand));

export const isFemaCredentialInspection = (value: unknown): value is FemaCredentialInspection =>
  isRecord(value) &&
  isString(value.subject) &&
  isString(value.issuer) &&
  isString(value.serialNumber) &&
  isString(value.sha256) &&
  SHA256_PATTERN.test(value.sha256) &&
  isString(value.notBefore) &&
  isString(value.notAfter) &&
  isInteger(value.daysToExpiry) &&
  isString(value.keyAlgorithm) &&
  isInteger(value.chainLength) &&
  value.chainLength >= 0 &&
  (value.cogMatches === null || typeof value.cogMatches === 'boolean');

const isReplacementFacts = (
  value: unknown,
): value is FemaCredentialFacts & { validatedAt: string | null } =>
  isFemaCredentialFacts(value) && isStringOrNull((value as UnknownRecord).validatedAt);

export const isFemaTenantSummary = (value: unknown): value is FemaTenantSummary =>
  isRecord(value) &&
  isInteger(value.id) &&
  value.id > 0 &&
  isFemaEnvironmentClass(value.environment) &&
  typeof value.isProduction === 'boolean' &&
  isString(value.cogId) &&
  isStringOrNull(value.cogName) &&
  typeof value.enabled === 'boolean' &&
  isFemaCredentialState(value.credentialState) &&
  isIntegerOrNull(value.daysToExpiry) &&
  (value.expiryBand === null || isFemaExpiryBand(value.expiryBand)) &&
  isStringOrNull(value.lastValidatedAt) &&
  (value.failedStage === null || isFemaCredentialStage(value.failedStage)) &&
  (value.active === null || isFemaCredentialFacts(value.active)) &&
  (value.replacement === null || isReplacementFacts(value.replacement));

export const isFemaCredentialInspectAnswer = (
  value: unknown,
): value is FemaCredentialInspectAnswer =>
  isRecord(value) && value.stored === false && isFemaCredentialInspection(value.inspection);

export const isFemaCredentialStoreAnswer = (value: unknown): value is FemaCredentialStoreAnswer =>
  isRecord(value) &&
  isRecord(value.credential) &&
  (value.credential.slot === 'active' || value.credential.slot === 'pending') &&
  isInteger(value.credential.generation) &&
  value.credential.status === 'stored' &&
  isFemaCredentialFacts(value.credential.certificate) &&
  isFemaTenantSummary(value.tenant);

export const isFemaTenantValidation = (value: unknown): value is FemaTenantValidation =>
  isRecord(value) &&
  (value.state === 'passed' || value.state === 'failed') &&
  isStringOrNull(value.at) &&
  (value.slot === 'active' || value.slot === 'pending') &&
  (value.stage === null || isFemaCredentialStage(value.stage)) &&
  isStringOrNull(value.error);

export const isFemaTenantValidateAnswer = (value: unknown): value is FemaTenantValidateAnswer =>
  isRecord(value) && isFemaTenantSummary(value.tenant) && isFemaTenantValidation(value.validation);

export const isFemaTenantRefusal = (value: unknown): value is FemaTenantRefusal =>
  isRecord(value) &&
  isString(value.code) &&
  isString(value.message) &&
  (value.stage === undefined || isFemaCredentialStage(value.stage)) &&
  (value.findings === undefined || Array.isArray(value.findings));
